import { ref } from "vue";

import type {
  AskStreamEvent,
  IngestStatus,
  CandidatesResponse,
  CheckInboxResponse,
  CheckResultResponse,
  JobStartResponse,
  TicketDetail,
  AccessListEntry,
  AccessListResponse,
  AccessRole,
  ApiTokenCreatedResponse,
  ApiTokenListResponse,
  GitlabUserSearchResponse,
  CurrentUser,
  CurrentUserResponse,
  MrDashboardResult,
  MrDetailResult,
  ReleaseBoardResponse,
  ReleaseListResponse,
  ReviewStatusResponse,
  SandboxesResponse,
  SearchResponse,
  SprintBoardResponse,
  TriageResponse,
  InfraStatus,
  CursorBilling,
  LatestSnapshot,
  BoardBanner,
  BoardBannersResponse,
  PublishBoardBannerBody,
} from "./types";

/**
 * Thin fetch wrapper over the Hono API. Errors surface as thrown `Error`s so
 * vue-query owns the retry/loading state — the Result pattern stops at the
 * HTTP boundary.
 */

/**
 * The status of the most recent failed request, cleared by the next success.
 *
 * An Error carries only a sentence, and every page passes that sentence to
 * `DataError` — so the panel could not tell "the server broke" from "you are
 * not allowed", and offered "Try again" for both. Retrying a 403 fails
 * forever. Kept here rather than threaded through eight call sites, for the
 * same reason identity is (`useCurrentUser`): it is one value per tab.
 */
export const lastFailureStatus = ref<number | null>(null);

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    lastFailureStatus.value = response.status;
    const message =
      payload !== null &&
      typeof payload === "object" &&
      "error" in payload &&
      typeof payload.error === "string"
        ? payload.error
        : `${response.status} ${response.statusText}`;
    throw new Error(message);
  }
  lastFailureStatus.value = null;
  return payload as T;
}

/**
 * `day_start` is the browser's own midnight. "Approvals I gave today"
 * (CTF-9324) is a local-time question, and the server has no way to know this
 * tab's offset, so the client states it.
 */
export function fetchMrDashboard(refresh = false): Promise<MrDashboardResult> {
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const query = new URLSearchParams({ day_start: midnight.toISOString() });
  if (refresh) query.set("refresh", "1");
  return request<MrDashboardResult>(`/api/mrs?${query.toString()}`);
}

/** Stored diff for one MR. The board list does not carry patches. */
export function fetchMrDetail(iid: number): Promise<MrDetailResult> {
  return request<MrDetailResult>(`/api/mrs/${iid}`);
}

/**
 * Watch or unwatch one MR (CTF-9318). Idempotent on both sides, so the caller
 * can send the desired state rather than a flip.
 */
export function setMrWatch(
  iid: number,
  watching: boolean,
): Promise<{ watching: boolean }> {
  return request<{ watching: boolean }>(`/api/mrs/${iid}/watch`, {
    method: watching ? "PUT" : "DELETE",
  });
}

/** Latest triage worklist — a local read, no GitLab calls, no model calls. */
export function fetchTriage(): Promise<TriageResponse> {
  return request<TriageResponse>("/api/triage");
}

/**
 * Queues a triage pass and returns at once. `refresh` re-reads GitLab first;
 * `limit` bounds how many top MRs get the LLM brief.
 *
 * The pass belongs to the server, so the tab can close and a second person
 * sees the same progress. Poll `fetchTriage` for `job` and the finished run.
 */
export function runTriage(
  limit?: number,
  refresh = false,
): Promise<JobStartResponse> {
  const query = new URLSearchParams();
  if (refresh) query.set("refresh", "1");
  if (limit) query.set("limit", String(limit));
  const suffix = query.toString();
  return request<JobStartResponse>(`/api/triage${suffix ? `?${suffix}` : ""}`, {
    method: "POST",
  });
}

export function searchKnowledge(
  question: string,
  topK: number,
  collections: string[],
): Promise<SearchResponse> {
  return request<SearchResponse>("/api/search", {
    method: "POST",
    body: JSON.stringify({ question, top_k: topK, collections }),
  });
}

export function fetchSprintBoard(
  board: string,
  refresh = false,
): Promise<SprintBoardResponse> {
  const query = new URLSearchParams({ board });
  if (refresh) query.set("refresh", "1");
  return request<SprintBoardResponse>(`/api/sprint?${query.toString()}`);
}

export function fetchReviewStatus(iid: number): Promise<ReviewStatusResponse> {
  return request<ReviewStatusResponse>(`/api/reviews/${iid}`);
}

/**
 * Queues a review. A 409 means one is already in flight, which is not a
 * failure — the caller just starts polling — so it resolves rather than throws.
 */
export async function startReview(
  iid: number,
): Promise<{ alreadyRunning: boolean }> {
  const response = await fetch(`/api/review/${iid}`, { method: "POST" });
  if (response.status === 409) return { alreadyRunning: true };
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const message =
      payload !== null &&
      typeof payload === "object" &&
      "error" in payload &&
      typeof payload.error === "string"
        ? payload.error
        : `${response.status} ${response.statusText}`;
    throw new Error(message);
  }
  return { alreadyRunning: false };
}

export function fetchCandidates(params: {
  status?: string;
  source?: string;
}): Promise<CandidatesResponse> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.source) query.set("source", params.source);
  const suffix = query.toString();
  return request<CandidatesResponse>(
    `/api/palace/candidates${suffix ? `?${suffix}` : ""}`,
  );
}

export function promoteCandidate(id: number): Promise<unknown> {
  return request(`/api/palace/candidates/${id}/promote`, { method: "POST" });
}

export function rejectCandidate(id: number): Promise<unknown> {
  return request(`/api/palace/candidates/${id}/reject`, { method: "POST" });
}

/** Per-source corpus freshness. A cheap local read, safe to poll. */
export function fetchIngestStatus(): Promise<IngestStatus> {
  return request<IngestStatus>("/api/ingest/status");
}

/**
 * Read an SSE POST. `EventSource` is GET-only, and both Ask and Check send
 * the body as JSON, so the frames are parsed by hand.
 */
async function readSse<T>(
  path: string,
  body: unknown,
  onEvent: (event: T) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok || !response.body) {
    const payload: unknown = await response.json().catch(() => null);
    const message =
      payload !== null &&
      typeof payload === "object" &&
      "error" in payload &&
      typeof payload.error === "string"
        ? payload.error
        : `${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let split = buffer.indexOf("\n\n");
    while (split !== -1) {
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      const data = frame
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim())
        .join("");
      if (data) {
        try {
          onEvent(JSON.parse(data) as T);
        } catch {
          // A frame we cannot parse is dropped rather than aborting a stream
          // that is otherwise delivering an answer.
        }
      }
      split = buffer.indexOf("\n\n");
    }
  }
}

export async function askKnowledgeStream(
  body: {
    question: string;
    top_k: number;
    collections: string[];
    no_plan?: boolean;
  },
  onEvent: (event: AskStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  return readSse("/api/ask/stream", body, onEvent, signal);
}

/**
 * The stored check for a ticket, plus the run producing the next one.
 *
 * A check is 20-60s of model time and the answer holds until the ticket
 * changes, so the page reads the stored report rather than re-running it.
 */
export function fetchCheckResult(issueKey: string): Promise<CheckResultResponse> {
  return request<CheckResultResponse>(
    `/api/check/result/${encodeURIComponent(issueKey)}`,
  );
}

/** Queue a check. Returns at once; poll `fetchCheckResult` for progress. */
export function startCheck(issueKey: string): Promise<JobStartResponse> {
  return request<JobStartResponse>(
    `/api/check/result/${encodeURIComponent(issueKey)}`,
    { method: "POST" },
  );
}

/** The intake feed: open tickets carrying a trigger label. A cheap local read. */
export function fetchCheckInbox(): Promise<CheckInboxResponse> {
  return request<CheckInboxResponse>("/api/check/inbox");
}

/** One ticket's stored detail — description, reporter, comments. DB read. */
export function fetchTicketDetail(issueKey: string): Promise<TicketDetail> {
  return request<TicketDetail>(
    `/api/check/ticket/${encodeURIComponent(issueKey)}`,
  );
}

/** Pull reporter + comments from Jira into the DB, then return the detail. */
export function refreshTicketDetail(issueKey: string): Promise<TicketDetail> {
  return request<TicketDetail>(
    `/api/check/ticket/${encodeURIComponent(issueKey)}/refresh`,
    { method: "POST" },
  );
}

// ── CTF-9303: identity ──────────────────────────────────────────────────────

/**
 * Who is signed in, or null. A 401 is the normal answer for a visitor, not an
 * error, so it resolves rather than throws.
 */
export async function fetchCurrentUser(): Promise<CurrentUserResponse | null> {
  const response = await fetch("/api/auth/me");
  if (response.status === 401) return null;
  if (!response.ok)
    throw new Error(`${response.status} ${response.statusText}`);
  return (await response.json()) as CurrentUserResponse;
}

export function signOut(): Promise<unknown> {
  return request("/api/auth/session", { method: "DELETE" });
}

/** Which sign-in paths the server offers. Token sign-in is off. */
export interface AuthMethods {
  oauth: boolean;
  token: boolean;
  /** True on the public host: the SPA must not render data without a session. */
  required: boolean;
}

export function fetchAuthMethods(): Promise<AuthMethods> {
  return request<AuthMethods>("/api/auth/methods");
}

// ── CTF-9331: the access list ───────────────────────────────────────────────
//
// Admin-only on the server. The UI hides these controls from a plain user, but
// hiding is decoration: the 403 is the control.

export function fetchAccessList(): Promise<AccessListResponse> {
  return request<AccessListResponse>("/api/access");
}

/** Adds someone, or changes the role of someone already listed. */
export function grantAccess(
  username: string,
  role: AccessRole,
): Promise<{ user: AccessListEntry }> {
  return request<{ user: AccessListEntry }>("/api/access", {
    method: "POST",
    body: JSON.stringify({ username, role }),
  });
}

/**
 * People matching `query`, for the access-list picker. Below two characters
 * the server returns nothing rather than the whole directory, so the caller
 * need not debounce for correctness — only for politeness.
 */
export function searchGitlabUsers(
  query: string,
): Promise<GitlabUserSearchResponse> {
  return request<GitlabUserSearchResponse>(
    `/api/access/gitlab-users?q=${encodeURIComponent(query)}`,
  );
}

/** Lets a plain user publish MR-board banners, or stops them (CTF-9315). */
export function setBannerEditor(
  username: string,
  bannerEditor: boolean,
): Promise<{ bannerEditor: boolean }> {
  return request<{ bannerEditor: boolean }>(
    `/api/access/${encodeURIComponent(username)}`,
    { method: "PATCH", body: JSON.stringify({ bannerEditor }) },
  );
}

export function revokeAccess(username: string): Promise<{ removed: boolean }> {
  return request<{ removed: boolean }>(
    `/api/access/${encodeURIComponent(username)}`,
    { method: "DELETE" },
  );
}

// ── CTF-9340: personal API tokens ───────────────────────────────────────────
//
// Cookie-authenticated, always. The server refuses these routes to a bearer
// caller, so a token cannot mint another token.

export function fetchApiTokens(): Promise<ApiTokenListResponse> {
  return request<ApiTokenListResponse>("/api/auth/tokens");
}

/** The secret comes back once, in this response, and is never stored. */
export function createApiToken(name: string): Promise<ApiTokenCreatedResponse> {
  return request<ApiTokenCreatedResponse>("/api/auth/tokens", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export function revokeApiToken(id: string): Promise<{ revoked: boolean }> {
  return request<{ revoked: boolean }>(
    `/api/auth/tokens/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export function fetchSandboxes(refresh = false): Promise<SandboxesResponse> {
  const query = refresh ? "?refresh=1" : "";
  return request<SandboxesResponse>(`/api/sandboxes${query}`);
}

export function fetchReleaseList(): Promise<ReleaseListResponse> {
  return request<ReleaseListResponse>("/api/releases");
}

export function fetchReleaseBoard(
  version: string,
): Promise<ReleaseBoardResponse> {
  return request<ReleaseBoardResponse>(
    `/api/releases/${encodeURIComponent(version)}`,
  );
}

export function fetchInfraStatus(): Promise<InfraStatus> {
  return request<InfraStatus>("/api/settings/infra");
}

export function fetchCursorBilling(): Promise<CursorBilling> {
  return request<CursorBilling>("/api/settings/billing");
}

/** The nightly database snapshot. The file itself is a plain link, not fetch. */
export function fetchLatestSnapshot(): Promise<LatestSnapshot> {
  return request<LatestSnapshot>("/api/data/snapshot");
}

/** Active (and, for editors, scheduled) board banners (CTF-9315). */
export function fetchBoardBanners(): Promise<BoardBannersResponse> {
  return request<BoardBannersResponse>("/api/banners");
}

export function publishBoardBanner(
  body: PublishBoardBannerBody,
): Promise<BoardBanner> {
  return request<BoardBanner>("/api/banners", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateBoardBanner(
  id: string,
  body: PublishBoardBannerBody,
): Promise<BoardBanner> {
  return request<BoardBanner>(`/api/banners/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function endBoardBanner(id: string): Promise<BoardBanner> {
  return request<BoardBanner>(`/api/banners/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}
